import { MouseEvent, useCallback, useMemo, useState } from 'react';
import { JsonSchema, UISchemaElement } from '@jsonforms/core';
import { materialCells } from '@jsonforms/material-renderers';
import { JsonForms } from '@jsonforms/react';
import { useQuery } from '@tanstack/react-query';
import { Control, ControllerRenderProps, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as z from 'zod';
import { endpoints, getIDFromUrl, Url } from '@rossum/api-client';
import { SchemaSection } from '@rossum/api-client/schemas';
import { snakeToCamel } from '@rossum/api-client/utils';
import { ContentTitle } from '@rossum/components/content-group/ContentTitle';
import { ContentWrapper } from '@rossum/components/content-group/ContentWrapper';
import { IconDotsVertical } from '@rossum/ui/icons/tabler';
import {
  Alert,
  Chip,
  IconButton,
  LinearProgress,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { useApiClient } from '../../../../../lib/apiClient';
import { isNotNullOrUndefined } from '../../../../../lib/typeGuards';
import { renderers } from '../../../../extension-interop/custom-controls/renderers';
import { intentSchema } from '../../../../extension-interop/hooks/intent.schema';
import { useHookInterfaces } from '../../../../extension-interop/hooks/useHookInterfaces';
import { FieldsFormValues } from '../../form-model/formModels';
import { ComplexDataLookup } from './complex-data-lookup/ComplexDataLookup';
import { ComplexDataLookupPayload } from './complex-data-lookup/types';
import { customMatchingConfigurationSchema } from './utils';

const builtinLookups = ['master_data_hub'] as const;
type BuiltinLookupId = (typeof builtinLookups)[number];

export const useInvokeHookInterfaceQuery = ({
  hookInterface,
}: {
  hookInterface: Url | undefined;
}) => {
  const api = useApiClient();

  return useQuery({
    queryKey: ['invoke_hook_interface', hookInterface],
    enabled: hookInterface !== undefined,
    queryFn: async () => {
      if (hookInterface === undefined) {
        throw new Error('Hook interface is required');
      }

      const response = await api.request(
        endpoints.hookInterfaces.invoke(getIDFromUrl(hookInterface), {
          configure: true,
        })
      );
      return z
        .object({
          intent: intentSchema.optional(),
        })
        .parse(response);
    },
  });
};

type CustomLookupContentProps = {
  value: z.infer<typeof customMatchingConfigurationSchema>;
  onChange: (value: z.infer<typeof customMatchingConfigurationSchema>) => void;
  schemaContent: SchemaSection[];
  lookupSchemaId: string;
  lookupLabel: string;
};

export const CustomLookupContent = ({
  value,
  onChange,
  schemaContent,
  lookupSchemaId,
  lookupLabel,
}: CustomLookupContentProps) => {
  const intl = useIntl();

  const {
    data: response,
    isLoading,
    isFetching,
    isError,
    error,
  } = useInvokeHookInterfaceQuery({
    hookInterface: value.hook_interface,
  });

  const componentName = builtinLookups.find(
    id => id === (value.type ?? response?.intent?.component?.name)
  );

  return componentName ? (
    <BuiltinLookups
      componentName={componentName}
      value={value.configuration}
      onChange={(configuration: Record<string, unknown>) =>
        onChange({
          hook_interface: value.hook_interface,
          type: value.type,
          configuration,
        })
      }
      schemaContent={schemaContent}
      lookupSchemaId={lookupSchemaId}
      lookupLabel={lookupLabel}
    />
  ) : isFetching ? (
    <LinearProgress />
  ) : isLoading ? null : isError ? ( // isLoading here means idle = no hook interface
    <Alert color="error">{error?.toString()}</Alert>
  ) : !response.intent ? (
    <Alert color="error">
      {intl.formatMessage({
        id: 'features.queueSettings.rules.actions.documentAction.custom.errorIntent',
      })}
    </Alert>
  ) : (
    <>
      {response.intent.error ? (
        <Alert color="error">{response.intent.error.message}</Alert>
      ) : null}

      {response.intent.info ? (
        <Alert color="info">{response.intent.info.message}</Alert>
      ) : null}

      {response.intent.form ? (
        <JsonForms
          schema={response.intent?.form?.schema as JsonSchema}
          uischema={response.intent?.form?.uiSchema as UISchemaElement}
          data={value.configuration}
          validationMode="NoValidation"
          renderers={renderers}
          cells={materialCells}
          onChange={({ data }) => {
            if (data !== value.configuration) {
              onChange({
                hook_interface: value.hook_interface,
                configuration: data,
              });
            }
          }}
        />
      ) : null}
    </>
  );
};

type BuiltinLookupsProps = {
  componentName: BuiltinLookupId;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
  schemaContent: SchemaSection[];
  lookupSchemaId: string;
  lookupLabel: string;
};

export const BuiltinLookups = ({
  componentName,
  value,
  ...rest
}: BuiltinLookupsProps) => {
  return (
    <>
      {componentName === 'master_data_hub' ? (
        <ComplexDataLookup
          value={value as ComplexDataLookupPayload}
          {...rest}
        />
      ) : null}
    </>
  );
};

export const CustomLookup = ({
  field,
  schemaContent,
  control,
}: {
  field: ControllerRenderProps<FieldsFormValues, 'field.matching'>;
  schemaContent: SchemaSection[];
  control: Control<FieldsFormValues>;
}) => {
  const intl = useIntl();
  const { data: hookInterfaces } = useHookInterfaces({
    canInvoke: true,
    type: 'queue_lookup',
  });

  const datasetResult = customMatchingConfigurationSchema.safeParse(
    field.value
  );

  const lookupOptions = useMemo(
    () => [
      ...builtinLookups.filter(isNotNullOrUndefined).map(id => ({
        id,
        type: 'out_of_the_box' as const,
        name: intl.formatMessage({
          id: `features.queueSettings.fields.lookup.builtin.${snakeToCamel(id)}.title` as const,
        }),
        description: intl.formatMessage({
          id: `features.queueSettings.fields.lookup.builtin.${snakeToCamel(id)}.description` as const,
        }),
      })),
      ...(hookInterfaces?.results ?? []).map(i => ({
        ...i,
        type: 'hook_interface' as const,
      })),
    ],
    [hookInterfaces?.results, intl]
  );

  const value =
    lookupOptions.find(option =>
      datasetResult.success
        ? (option.type === 'hook_interface' &&
            datasetResult.data.hook_interface === option.url) ||
          (option.type === 'out_of_the_box' &&
            datasetResult.data.type === option.id)
        : false
    ) ?? null;

  const lookupSchemaId = useWatch({ control, name: 'field.id' });
  const lookupLabel = useWatch({ control, name: 'field.label' });

  const hasMultipleLookups =
    lookupOptions.length > 1 || field.value['type'] !== 'master_data_hub';

  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const handleMenuOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleMenuSelect = useCallback(
    (option: (typeof lookupOptions)[number] | null) => {
      field.onChange(
        option
          ? {
              hook_interface:
                option.type === 'hook_interface' ? option.url : undefined,
              type: option.type === 'out_of_the_box' ? option.id : undefined,
            }
          : {}
      );
      handleMenuClose();
    },
    [field, handleMenuClose]
  );

  return (
    <ContentWrapper>
      <Stack spacing={4}>
        <ContentTitle
          title={intl.formatMessage({
            id: 'features.queueSettings.fields.lookup.title',
          })}
          description={
            value ? (
              <Typography variant="body2">{value.description}</Typography>
            ) : (
              intl.formatMessage({
                id: 'features.queueSettings.fields.lookup.description',
              })
            )
          }
          titleAction={
            hasMultipleLookups ? (
              <>
                <IconButton size="small" onClick={handleMenuOpen}>
                  <SvgIcon fontSize="large">
                    <IconDotsVertical />
                  </SvgIcon>
                </IconButton>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  slotProps={{ paper: { sx: { maxWidth: 300 } } }}
                >
                  {lookupOptions.map(option => (
                    <MenuItem
                      key={option.id}
                      selected={value?.id === option.id}
                      onClick={() => handleMenuSelect(option)}
                    >
                      <ListItemText
                        primary={
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            sx={{ width: 'fit-content' }}
                          >
                            <span>{option.name}</span>
                            {option.type === 'hook_interface' ? (
                              <Chip
                                label={intl.formatMessage({
                                  id: 'features.queueSettings.fields.hookInterface.chip',
                                })}
                                size="small"
                              />
                            ) : null}
                          </Stack>
                        }
                        secondary={option.description}
                        secondaryTypographyProps={{
                          sx: { whiteSpace: 'normal' },
                        }}
                      />
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : undefined
          }
        />
        <Stack>
          {datasetResult.success ? (
            <CustomLookupContent
              value={datasetResult.data}
              onChange={field.onChange}
              schemaContent={schemaContent}
              lookupSchemaId={lookupSchemaId}
              lookupLabel={lookupLabel}
            />
          ) : null}
        </Stack>
      </Stack>
    </ContentWrapper>
  );
};
